import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCompass, FiMessageSquare, FiShield, FiTarget } from "react-icons/fi";

import PageHero from "../../components/PageHero/PageHero";
import Reveal from "../../components/Reveal/Reveal";
import SEO from "../../components/SEO/SEO";
import {
  aboutPillars,
  buyerPromises,
  companyInfo,
  pageImages,
} from "../../data/siteContent";

const valueCards = [
  {
    title: "Mission",
    description:
      "To connect international buyers with well-presented, eco-focused Indian products through professional trade execution.",
    icon: FiTarget,
  },
  {
    title: "Vision",
    description:
      "To become a respected sourcing partner known for premium presentation, disciplined follow-through, and relationship-led growth.",
    icon: FiCompass,
  },
  {
    title: "Quality mindset",
    description:
      "We pay attention to product suitability, packaging standards, and the details buyers notice before repeat orders happen.",
    icon: FiShield,
  },
  {
    title: "Communication style",
    description:
      "We prefer clear updates, realistic timelines, and responsive conversations that help buyers move with confidence.",
    icon: FiMessageSquare,
  },
];

export default function AboutUs() {
  return (
    <>
      <SEO
        title="About"
        description="Learn how Thamizhi International Ventures combines eco-focused sourcing, export discipline, and professional buyer communication for international trade partnerships."
        path="/about"
        image={pageImages.about}
      />

      <PageHero
        eyebrow="About us"
        title="About Us"
        description={`${companyInfo.name} brings together premium eco-product positioning, export-ready coordination, and a practical understanding of what international buyers need before they place repeat orders.`}
        actions={[
          { label: "Explore products", to: "/products", variant: "primary" },
          { label: "Contact our team", to: "/contact", variant: "secondary" },
        ]}
        stats={[
          { label: "Location", value: companyInfo.location },
          { label: "Core categories", value: "Bagasse, areca, and eco-led sourcing" },
          { label: "Approach", value: "Relationship-driven export support" },
        ]}
        media={
          <div className="overlap-visual overlap-visual--tall">
            <div className="overlap-visual__backdrop" aria-hidden="true" />
            <img src={pageImages.about} alt="About Thamizhi International Ventures" />
          </div>
        }
      />

      <section className="section-band">
        <div className="section-shell split-layout">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">How we work</p>
              <h2 className="section-title">Trade support that treats sourcing, presentation, and dispatch as one system</h2>
            </div>
            <p className="section-copy">
              We are not only interested in listing products. We are interested in building
              a reliable buyer experience that carries through inquiry, confirmation,
              packaging, and shipment readiness.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="content-list">
              {aboutPillars.map((pillar) => (
                <article key={pillar.title} className="content-list__item">
                  <h3>{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </article>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-band section-band--soft">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Our values</p>
              <h2 className="section-title">A premium business presence grounded in practical delivery</h2>
            </div>
          </Reveal>

          <div className="feature-grid">
            {valueCards.map((card, index) => {
              const Icon = card.icon;

              return (
                <Reveal key={card.title} className="feature-card" delay={index * 90}>
                  <div className="feature-card__icon">
                    <Icon aria-hidden="true" />
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-band">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Buyer promises</p>
              <h2 className="section-title">What international buyers can consistently expect from us</h2>
            </div>
          </Reveal>

          <div className="promise-grid">
            {buyerPromises.map((promise, index) => (
              <Reveal key={promise} className="promise-card" delay={index * 70}>
                <span>{`0${index + 1}`}</span>
                <p>{promise}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="section-cta-inline" delay={140}>
            <Link to="/services" className="button button-secondary">
              View our services
              <FiArrowRight aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
